import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { parseDateAndTime, normalizeTimeString } from '@/lib/dateTimeUtils';
import { processToastPayoutsFile, detectToastPayoutsFromHeaders } from './toastPayoutsProcessor';
import { processUberPayoutsFile, processUberDetailedOrdersFile } from './uberPayoutsProcessor';
import { buildHeaderMap, findCol } from './paymentsProcessor';
import { PLATFORMS } from '@/lib/platformConstants';

/**
 * Unified payouts processor that detects the platform and routes to the appropriate processor
 */

export interface ProcessedPayoutData {
  platform: string;
  source: string;
  location: string;
  payout_date?: string | null;
  /** When present, store separately so date and time columns are both populated */
  payout_time?: string | null;
  settled_date?: string | null;
  total_payments_amount?: number;
  net_payout_amount?: number;
  net_deposit_amount?: number;
  transaction_fees?: number;
  commission?: number;
  total_tips?: number;
  total_number_of_payments?: number;
  payout_reference_id?: string;
  payout_status?: string;
  // Allow additional platform-specific fields
  [key: string]: any;
}

/**
 * Detect platform from headers for payouts files
 * This handles the unique header structure of each platform's payouts report
 * Exported for Data Center upload flow (header-based file type detection).
 */
export function detectPayoutsPlatform(headers: any[], headers2?: any[]): 'toast' | 'uber_eats' | 'uber_eats_detailed' | 'doordash' | 'doordash_standalone' | null {
  // Filter out empty headers and normalize
  const filteredHeaders = headers.filter(h => h && String(h).trim() !== '');
  const headerStr = headers.map(h => String(h || '').toLowerCase().trim()).join('|');
  const filteredHeaderStr = filteredHeaders.map(h => String(h || '').toLowerCase().trim()).join('|');
  const headerStr2 = headers2 ? headers2.map(h => String(h || '').toLowerCase().trim()).join('|') : '';
  
  console.log(`Payouts detection - Headers row 1: ${filteredHeaderStr.substring(0, 200)}`);
  console.log(`Payouts detection - Headers row 2: ${headerStr2.substring(0, 200)}`);
  
  // Toast Payouts - Two known formats:
  // Old XLSX: [empty], Location, Settled Date, Batch Date(s), Sales Date(s), ..., Transaction Fees, Net Deposit Amount
  // New CSV: Settled date, Name, Location, Type, Sales period start, ..., Payments, ..., Fees, ..., Payout, Status
  const toastRequired = ['location', 'settled date'];
  const toastOptionalOld = ['batch date', 'sales date', 'transaction fees', 'net deposit', 'total payments'];
  const toastOptionalNew = ['payments', 'fees', 'payout', '# txns'];
  
  const hasToastRequired = toastRequired.every(col => 
    headerStr.includes(col) || filteredHeaderStr.includes(col)
  );
  const hasToastOptionalOld = toastOptionalOld.filter(col => 
    headerStr.includes(col) || filteredHeaderStr.includes(col)
  ).length >= 2;
  const hasToastOptionalNew = toastOptionalNew.filter(col => 
    headerStr.includes(col) || filteredHeaderStr.includes(col)
  ).length >= 2;
  
  if (hasToastRequired && (hasToastOptionalOld || hasToastOptionalNew)) {
    return 'toast';
  }
  
  // Uber Eats Detailed Order Report - Has description row 1 and headers in row 2
  // Row 1 contains descriptions like "Store name as per Uber Eats manager", "Order ID as per Uber Eats manager"
  // Row 2 contains actual headers: "Store Name", "Store ID", "Order ID", "Workflow ID", "Dining Mode", etc.
  const uberDetailedDescriptionMarkers = ['uber eats manager', 'uber eats mana'];
  const uberDetailedHeaders = ['store name', 'order id', 'total payout', 'payout date', 'marketplace fee'];
  
  const hasUberDetailedDescription = uberDetailedDescriptionMarkers.some(marker => 
    headerStr.includes(marker) || filteredHeaderStr.includes(marker)
  );
  const hasUberDetailedHeaders = uberDetailedHeaders.filter(col => headerStr2.includes(col)).length >= 4;
  
  if (hasUberDetailedDescription && hasUberDetailedHeaders) {
    return 'uber_eats_detailed';
  }
  
  // Also check if row 2 has detailed headers even without description markers
  // This handles files where row 1 might be slightly different
  if (headerStr2.includes('order id') && 
      headerStr2.includes('total payout') && 
      headerStr2.includes('marketplace fee') &&
      headerStr2.includes('payout date')) {
    return 'uber_eats_detailed';
  }
  
  // Uber Payouts - Uses aggregated payout report with specific headers
  // Headers: Store Name, Shop ID, Store ID, Order Count, Sales (excl. tax), Total payout, Payout Date
  const uberPayoutsHeaders = ['store name', 'order count', 'sales (excl. tax)', 'total payout', 'payout date'];
  // Check both row 1 and row 2 (Uber sometimes has description in row 1, headers in row 2)
  if (uberPayoutsHeaders.every(col => headerStr.includes(col)) || 
      uberPayoutsHeaders.every(col => headerStr2.includes(col))) {
    return 'uber_eats';
  }
  
  // DoorDash Standalone Payouts report (dedicated CSV with one row per payout period)
  // Headers: Business ID, Business name, Payout date, Net total, Payout ID, Payout status
  // Must be checked BEFORE the old payments-based DoorDash detection
  const doordashStandaloneRequired = ['business id', 'payout date', 'net total', 'payout id', 'payout status'];
  if (doordashStandaloneRequired.every(col => headerStr.includes(col))) {
    return 'doordash_standalone';
  }
  
  // DoorDash payouts derived from the payments/detailed transactions file
  // Old XLSX: has "payout time" as separate column
  // New CSV: has "payout date" + "business name" + "net total" (no separate payout time)
  const doordashOldHeaders = ['payout date', 'payout time', 'net total', 'business name'];
  const doordashNewHeaders = ['payout date', 'net total', 'business name', 'commission', 'subtotal'];
  if (doordashOldHeaders.every(col => headerStr.includes(col)) ||
      doordashNewHeaders.every(col => headerStr.includes(col))) {
    return 'doordash';
  }
  
  return null;
}

/**
 * Process DoorDash payouts data - extract payout-related info from detailed transactions.
 * DoorDash uses the same file for payments and payouts - we aggregate by payout date.
 * Uses header-based column lookup to support all DoorDash CSV/XLSX format variants.
 */
async function processDoorDashPayouts(rows: any[][]): Promise<ProcessedPayoutData[]> {
  const payoutsByDate = new Map<string, ProcessedPayoutData>();
  const headerRow = rows[0] || [];
  const headerMap = buildHeaderMap(headerRow);

  const colPayoutDate = findCol(headerMap, 'payout date');
  const colPayoutTime = findCol(headerMap, 'payout time');
  const colBusinessName = findCol(headerMap, 'business name');
  const colTransactionType = findCol(headerMap, 'transaction type');
  const colNetTotal = findCol(headerMap, 'net total');
  const colCommission = findCol(headerMap, 'commission');
  const colMarketingFees = findCol(headerMap, 'marketing fees');

  if (colPayoutDate === undefined || colNetTotal === undefined) {
    console.warn('DoorDash Payouts: Missing required columns (payout date or net total)');
    return [];
  }

  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || !row[colPayoutDate]) continue;
    
    if (colTransactionType !== undefined) {
      const txType = String(row[colTransactionType] || '').trim();
      if (txType === 'Error Charge') continue;
    }
    
    const payoutDate = row[colPayoutDate];
    const payoutTimeRaw = colPayoutTime !== undefined ? row[colPayoutTime] : undefined;
    const location = colBusinessName !== undefined ? String(row[colBusinessName] || '').trim() : '';
    const netTotal = parseFloat(row[colNetTotal]) || 0;
    const commission = colCommission !== undefined ? Math.abs(parseFloat(row[colCommission]) || 0) : 0;
    const marketingFees = colMarketingFees !== undefined ? Math.abs(parseFloat(row[colMarketingFees]) || 0) : 0;
    
    if (payoutDate === 'NULL' || payoutDate === '' || payoutDate === null || payoutDate === undefined) {
      continue;
    }
    const parsedPayout = parseDateAndTime(payoutDate);
    const parsedPayoutDate = parsedPayout.date;
    if (!parsedPayoutDate) continue;

    const parsedPayoutTime =
      payoutTimeRaw != null && String(payoutTimeRaw).trim() !== ''
        ? normalizeTimeString(payoutTimeRaw)
        : null;

    const key = `${parsedPayoutDate}-${location}`;
    
    if (!payoutsByDate.has(key)) {
      payoutsByDate.set(key, {
        platform: PLATFORMS.DOORDASH,
        source: 'DoorDash YTD Payouts',
        location,
        payout_date: parsedPayoutDate,
        payout_time: parsedPayoutTime,
        net_payout_amount: 0,
        commission: 0,
        transaction_fees: 0,
        total_number_of_payments: 0,
      });
    }
    
    const payout = payoutsByDate.get(key)!;
    payout.net_payout_amount = (payout.net_payout_amount || 0) + netTotal;
    payout.commission = (payout.commission || 0) + commission;
    payout.transaction_fees = (payout.transaction_fees || 0) + marketingFees;
    payout.total_number_of_payments = (payout.total_number_of_payments || 0) + 1;
  }
  
  console.log(`DoorDash Payouts: Aggregated ${payoutsByDate.size} payout records`);
  return Array.from(payoutsByDate.values());
}

/**
 * Process DoorDash standalone payouts CSV (one row per payout period).
 * Column mapping (0-indexed):
 *  0 (A): Business ID
 *  1 (B): Business name -> location
 *  5 (F): Payout date -> payout_date
 *  8 (I): Subtotal -> gross_sales
 * 10 (K): Staff tip -> total_tips
 * 11 (L): Commission (negative) -> commission
 * 13 (N): Marketing fees -> transaction_fees
 * 21 (V): Net total -> net_payout_amount (bank deposit amount)
 * 25 (Z): Payout ID -> payout_reference_id
 * 26 (AA): Payout status -> payout_status
 */
function processDoorDashStandalonePayouts(rows: any[][]): ProcessedPayoutData[] {
  const results: ProcessedPayoutData[] = [];

  // Build a header-index map from row 0 for resilient column lookup
  const headerRow = rows[0] || [];
  const headerMap: Record<string, number> = {};
  headerRow.forEach((h: any, idx: number) => {
    if (h != null) headerMap[String(h).toLowerCase().trim()] = idx;
  });

  const col = (name: string, fallback: number): number => headerMap[name] ?? fallback;
  const colLocation     = col('business name', 1);
  const colPayoutDate   = col('payout date', 5);
  const colSubtotal     = col('subtotal', 8);
  const colStaffTip     = col('staff tip', 10);
  const colCommission   = col('commission', 11);
  const colMarketingFee = headerMap['marketing fees | (including any applicable taxes)']
    ?? headerMap['marketing fees']
    ?? 13;
  const colNetTotal     = col('net total', 21);
  const colPayoutId     = col('payout id', 25);
  const colPayoutStatus = col('payout status', 26);

  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.length < 6) continue;

    const payoutDateRaw = row[colPayoutDate];
    if (!payoutDateRaw || payoutDateRaw === 'NULL' || String(payoutDateRaw).trim() === '') continue;

    if (!payoutDateRaw || payoutDateRaw === 'NULL' || String(payoutDateRaw).trim() === '') {
      continue;
    }
    const parsedPayout = parseDateAndTime(payoutDateRaw);
    const parsedPayoutDate = parsedPayout.date;
    if (!parsedPayoutDate) continue;

    const parseNum = (v: any): number => {
      if (v == null || v === '') return 0;
      const n = typeof v === 'string' ? parseFloat(v.replace(/[,$]/g, '')) : Number(v);
      return isNaN(n) ? 0 : n;
    };

    results.push({
      platform: PLATFORMS.DOORDASH,
      source: 'DoorDash Payouts Report',
      location: String(row[colLocation] || '').trim(),
      payout_date: parsedPayoutDate,
      net_payout_amount: parseNum(row[colNetTotal]),
      gross_sales: parseNum(row[colSubtotal]),
      commission: Math.abs(parseNum(row[colCommission])),
      transaction_fees: Math.abs(parseNum(row[colMarketingFee])),
      total_tips: parseNum(row[colStaffTip]),
      payout_reference_id: String(row[colPayoutId] || '').trim(),
      payout_status: String(row[colPayoutStatus] || '').trim(),
    });
  }

  console.log(`DoorDash Standalone Payouts: Parsed ${results.length} payout records`);
  return results;
}

/**
 * Main processor for payouts files
 * Automatically detects the platform and processes accordingly
 */
export async function processPayoutsFile(file: File): Promise<ProcessedPayoutData[]> {
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  let rows: any[][] = [];
  let rows2: any[] | undefined;
  
  // Parse file
  if (fileExtension === 'csv') {
    const text = await file.text();
    const result = Papa.parse(text, { header: false });
    rows = result.data as any[][];
    if (rows.length > 1) {
      rows2 = rows[1];
    }
  } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer);
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as any[][];
    if (rows.length > 1) {
      rows2 = rows[1];
    }
  } else {
    throw new Error('Unsupported file format. Please upload CSV or Excel files.');
  }
  
  // Detect platform from headers (check both row 1 and row 2)
  const platform = detectPayoutsPlatform(rows[0] || [], rows2);
  
  if (!platform) {
    // Provide helpful error message
    const detectedHeaders = (rows[0] || []).slice(0, 10).map(h => String(h || '').substring(0, 30)).join(', ');
    throw new Error(
      `Could not detect platform from file headers. ` +
      `Expected a Toast Payouts, Uber Eats Payouts, or DoorDash file. ` +
      `Found headers: ${detectedHeaders}...`
    );
  }
  
  console.log(`Payouts processor detected platform: ${platform}`);
  
  // Route to appropriate processor
  switch (platform) {
    case 'toast':
      const toastResult = await processToastPayoutsFile(file);
      // Normalize Toast output to common format
      return toastResult.map(t => ({
        ...t,
        payout_date: t.settled_date,
        net_payout_amount: t.net_deposit_amount,
      }));
      
    case 'uber_eats':
      const uberResult = await processUberPayoutsFile(file);
      // Normalize Uber output to common format
      return uberResult.map(t => ({
        ...t,
        payout_date: t.payout_date,
      }));
      
    case 'uber_eats_detailed':
      // Process Uber Eats detailed order-level report and aggregate by payout date
      const uberDetailedResult = await processUberDetailedOrdersFile(file);
      return uberDetailedResult.map(t => ({
        ...t,
        payout_date: t.payout_date,
      }));
      
    case 'doordash_standalone':
      return processDoorDashStandalonePayouts(rows);
      
    case 'doordash':
      return processDoorDashPayouts(rows);
      
    default:
      throw new Error('Unknown platform');
  }
}

/**
 * Map ProcessedPayoutData to a row suitable for sales_transactions insert/upsert.
 * Sets order_date/order_time (upsert key) in addition to sale_date/sale_time/payout_date.
 */
export function mapPayoutToSalesTransaction(p: ProcessedPayoutData): Record<string, unknown> {
  const rawPayoutDate = p.payout_date;
  const parsed = rawPayoutDate != null ? parseDateAndTime(rawPayoutDate) : { date: null, time: null };
  const payoutDate = parsed.date;
  const saleTime =
    p.payout_time != null && p.payout_time !== ''
      ? normalizeTimeString(p.payout_time)
      : parsed.time;
  const amount = p.net_deposit_amount ?? p.net_payout_amount ?? 0;

  // Generate a synthetic order_time when no real time exists.
  // This prevents upsert collisions on (company_id, platform, order_date, order_time)
  // and ensures ON CONFLICT can match rows (NULL order_time breaks ON CONFLICT in PG).
  let orderTime = saleTime;
  if (!orderTime) {
    const seed = p.payout_reference_id
      || `${p.platform}|${p.location}|${payoutDate}|${amount}|${p.transaction_fees ?? 0}|${p.total_number_of_payments ?? 0}|${p.batch_dates ?? ''}|${p.batch_numbers ?? ''}`;
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
    }
    const absHash = Math.abs(hash);
    const h = String(absHash % 24).padStart(2, '0');
    const m = String(Math.floor(absHash / 24) % 60).padStart(2, '0');
    const s = String(Math.floor(absHash / 1440) % 60).padStart(2, '0');
    orderTime = `${h}:${m}:${s}`;
  }

  const row: Record<string, unknown> = {
    platform: p.platform,
    source: p.source ?? 'pos_training',
    location: p.location ?? '',
    order_date: payoutDate,
    order_time: orderTime,
    payout_date: payoutDate,
    sale_date: payoutDate,
    sale_time: saleTime,
    net_deposit_amount: p.net_deposit_amount ?? null,
    net_payout_amount: p.net_payout_amount ?? null,
    gross_sales: amount,
    transaction_fees: p.transaction_fees ?? null,
    commission: p.commission ?? null,
    tip_amount: p.total_tips ?? null,
    total_number_of_payments: p.total_number_of_payments ?? null,
    payout_reference_id: p.payout_reference_id ?? null,
  };

  // Map Uber-specific fields when present (from ProcessedPayoutData [key: string]: any)
  const uberFields: [string, string][] = [
    ['bag_fee', 'bag_fee'],
    ['order_processing_fee', 'order_processing_fee'],
    ['delivery_network_fee', 'delivery_network_fee'],
    ['marketplace_facilitator_tax', 'marketplace_facilitator_tax'],
    ['backup_withholding_tax', 'backup_withholding_tax'],
    ['price_adjustments', 'price_adjustments'],
    ['tax_on_refunds', 'tax_on_refunds'],
    ['promotions_on_delivery', 'promotions_on_delivery'],
    ['order_count', 'order_count'],
    ['store_id', 'store_id'],
    ['shop_id', 'shop_id'],
  ];
  for (const [src, dest] of uberFields) {
    if (p[src] != null) row[dest] = p[src];
  }

  return row;
}
